import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, FileText } from '@phosphor-icons/react'
import { ORG_DOCS, EQUIPMENT_DOCS, type SourceDoc } from '../lib/source-data'

function DocCard({ doc }: { doc: SourceDoc }) {
  const navigate = useNavigate()

  return (
    <div
      className="handout-card handout-card--clickable"
      onClick={() => navigate(`/reference/sources/${doc.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/reference/sources/${doc.id}`)
        }
      }}
    >
      <div className="handout-card__icon">
        <FileText size={24} weight="fill" />
      </div>
      <div className="handout-card__body">
        <h3 className="handout-card__title">{doc.label}</h3>
        <p className="handout-card__desc">{doc.description}</p>
      </div>
      <a
        href={doc.pdf}
        download
        className="handout-card__action"
        title="Download PDF"
        onClick={(e) => e.stopPropagation()}
      >
        <Download size={20} weight="bold" />
      </a>
    </div>
  )
}

function matches(doc: SourceDoc, q: string) {
  if (!q) return true
  return (
    doc.label.toLowerCase().includes(q) ||
    doc.description.toLowerCase().includes(q) ||
    doc.id.toLowerCase().includes(q)
  )
}

export default function SourcesPage() {
  const [filter, setFilter] = useState('')

  const q = filter.toLowerCase().trim()
  const filteredOrg = useMemo(() => ORG_DOCS.filter((d) => matches(d, q)), [q])
  const filteredEquipment = useMemo(() => EQUIPMENT_DOCS.filter((d) => matches(d, q)), [q])
  const totalMatches = filteredOrg.length + filteredEquipment.length

  return (
    <div className="reference-page">
      <header className="crew-header">
        <p className="eyebrow">Reference</p>
        <h1>Source Documents</h1>
        <p className="lead">
          Official organization guidelines and equipment manuals — printable PDFs.
        </p>
      </header>

      <div className="reference-controls">
        <input
          type="search"
          placeholder="Search source docs…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="reference-search"
        />
      </div>

      {filteredOrg.length > 0 && (
        <section className="reference-section">
          <h2>Organization (CO-* Documents)</h2>
          <div className="handout-grid">
            {filteredOrg.map((doc) => (
              <DocCard key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      {filteredEquipment.length > 0 && (
        <section className="reference-section">
          <h2>Equipment Manuals</h2>
          <div className="handout-grid">
            {filteredEquipment.map((doc) => (
              <DocCard key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      {totalMatches === 0 && (
        <p className="reference-empty">No source docs match your search.</p>
      )}
    </div>
  )
}
